import * as React from 'react';
import { Autocomplete } from '@mui/material';
import moment from 'moment-timezone';
import { TextInput } from '@coverright/ui/inputs';

const TIMEZONE_IANA_LIST = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Sao_Paulo',
  'Atlantic/Azores',
  'Europe/London',
  'Europe/Paris',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Tbilisi',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Sydney',
  'Pacific/Auckland',
];

const tzName = (iana: string) =>
  Intl.DateTimeFormat('en', { timeZone: iana, timeZoneName: 'longGeneric' })
    .formatToParts()
    .find((p) => p.type === 'timeZoneName')?.value ?? iana;

const tzCity = (iana: string) =>
  iana.split('/').pop()?.replace(/_/g, ' ') ?? iana;

const formatTzLabel = (iana: string) =>
  `(GMT${moment.tz(iana).format('Z')}) ${tzName(iana)} - ${tzCity(iana)}`;

interface CalendarTimezoneSelectProps {
  value: string;
  onChange: (iana: string) => void;
}

export const CalendarTimezoneSelect = ({ value, onChange }: CalendarTimezoneSelectProps) => {
  const options = React.useMemo(() => {
    const base = [...TIMEZONE_IANA_LIST];
    if (value && !base.includes(value)) base.unshift(value);
    return base;
  }, [value]);

  return (
    <Autocomplete
      options={options}
      value={value || undefined}
      disableClearable
      autoHighlight
      getOptionLabel={(iana) => formatTzLabel(iana)}
      isOptionEqualToValue={(option, val) => option === val}
      onChange={(_, newValue) => { if (newValue) onChange(newValue); }}
      renderInput={(params) => (
        <TextInput
          hideTick
          validate={false}
          label="Timezone *"
          placeholder="Search timezone…"
          {...params}
        />
      )}
    />
  );
};
